/**
 * Request tracing utilities for observability
 *
 * Provides lightweight tracing that can be used with or without Sentry.
 */

export interface Span {
  name: string;
  startTime: number;
  endTime?: number;
  attributes: Record<string, string | number | boolean>;
  status: 'ok' | 'error';
  error?: string;
  children: Span[];
}

export class Tracer {
  private spans: Span[] = [];
  private currentSpan: Span | null = null;

  /**
   * Start a new span
   */
  startSpan(name: string, attributes: Record<string, string | number | boolean> = {}): Span {
    const span: Span = {
      name,
      startTime: Date.now(),
      attributes,
      status: 'ok',
      children: [],
    };

    if (this.currentSpan) {
      this.currentSpan.children.push(span);
    } else {
      this.spans.push(span);
    }

    return span;
  }

  /**
   * End a span
   */
  endSpan(span: Span, error?: Error): void {
    span.endTime = Date.now();
    if (error) {
      span.status = 'error';
      span.error = error.message;
    }
  }

  /**
   * Get all spans
   */
  getSpans(): Span[] {
    return this.spans;
  }

  /**
   * Get trace data as a JSON-serializable object
   */
  toJSON(): object {
    return {
      spans: this.spans.map(this.spanToJSON.bind(this)),
      totalDuration: this.getTotalDuration(),
    };
  }

  private spanToJSON(span: Span): object {
    return {
      name: span.name,
      duration: span.endTime ? span.endTime - span.startTime : undefined,
      attributes: span.attributes,
      status: span.status,
      error: span.error,
      children: span.children.map(this.spanToJSON.bind(this)),
    };
  }

  /**
   * Get total duration of all root spans
   */
  getTotalDuration(): number {
    return this.spans.reduce((total, span) => {
      const duration = span.endTime ? span.endTime - span.startTime : 0;
      return total + duration;
    }, 0);
  }

  /**
   * Log trace summary to console
   */
  logSummary(): void {
    for (const span of this.spans) {
      this.logSpan(span, 0);
    }
  }

  private logSpan(span: Span, depth: number): void {
    const indent = '  '.repeat(depth);
    const duration = span.endTime ? `${span.endTime - span.startTime}ms` : 'in progress';
    const status = span.status === 'error' ? ' [ERROR]' : '';
    console.log(`${indent}${span.name}: ${duration}${status}`);

    for (const child of span.children) {
      this.logSpan(child, depth + 1);
    }
  }
}

/**
 * Execute a function with automatic span tracking
 */
export async function withSpan<T>(
  tracer: Tracer,
  name: string,
  fn: () => Promise<T>,
  attributes: Record<string, string | number | boolean> = {}
): Promise<T> {
  const span = tracer.startSpan(name, attributes);
  try {
    const result = await fn();
    tracer.endSpan(span);
    return result;
  } catch (error) {
    tracer.endSpan(span, error as Error);
    throw error;
  }
}

/**
 * Create a tracer that can be attached to a request context
 */
export function createTracer(): Tracer {
  return new Tracer();
}
